import { useLayoutEffect, useRef, useState } from 'react';
import './BlendModeToggle.css';

/**
 * Two-state toggle for picking the blend-build mode.
 *
 * - "auto" → the engine balances proportions for the customer.
 * - "user_ratios" → the customer designs the proportions themselves
 *   (seeded with the engine's suggestion, then editable).
 *
 * The track uses an absolutely-positioned indicator that animates between
 * the two options. Indicator geometry is measured from the live DOM so the
 * pill always lines up with the active label, regardless of font / locale.
 */
function BlendModeToggle({ mode, onChange }) {
  const trackRef = useRef(null);
  const optionRefs = useRef({});
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 4, width: 0 });

  useLayoutEffect(() => {
    const track = trackRef.current;
    const active = optionRefs.current[mode];
    if (!track || !active) return;
    const trackRect = track.getBoundingClientRect();
    const activeRect = active.getBoundingClientRect();
    setIndicatorStyle({
      left: activeRect.left - trackRect.left,
      width: activeRect.width,
    });
  }, [mode]);

  const setRef = (key) => (el) => {
    if (el) optionRefs.current[key] = el;
  };

  return (
    <div className="blend-mode-toggle" role="group" aria-label="Blend mode">
      <div className="blend-mode-toggle__track" ref={trackRef}>
        <span
          className="blend-mode-toggle__indicator"
          style={{ left: indicatorStyle.left, width: indicatorStyle.width }}
          aria-hidden="true"
        />
        <button
          type="button"
          ref={setRef('auto')}
          className={`blend-mode-toggle__option${mode === 'auto' ? ' blend-mode-toggle__option--active' : ''}`}
          aria-pressed={mode === 'auto'}
          onClick={() => onChange('auto')}
        >
          Auto-balance
        </button>
        <button
          type="button"
          ref={setRef('user_ratios')}
          className={`blend-mode-toggle__option${mode === 'user_ratios' ? ' blend-mode-toggle__option--active' : ''}`}
          aria-pressed={mode === 'user_ratios'}
          onClick={() => onChange('user_ratios')}
        >
          Custom ratios
        </button>
      </div>
      <span
        className="blend-mode-toggle__help"
        role="img"
        aria-label="Auto-balance: we handle the proportions. Custom ratios: you control how much of each ingredient goes into your cup."
        tabIndex={0}
      >
        ?
        <span className="blend-mode-toggle__tooltip" aria-hidden="true">
          <strong>Auto-balance</strong>
          {' '}— we proportion the blend.
          <br />
          <strong>Custom ratios</strong>
          {' '}— you set how much of each botanical goes in the cup.
        </span>
      </span>
    </div>
  );
}

export default BlendModeToggle;
